import type * as lazyIndex from './lazyIndex';
import { LazyAction, LazyImport, LazyModule, registerLazyOrchestrator } from 'owa-bundling';
import type { LazyActionOptions } from 'owa-bundling-light';
import wrapLazy from './utils/wrapLazy';
import onAddinPictureDeleted from './actions/onAddinPictureDeleted';
import { afterCancelDelaySend } from 'owa-mail-delaysend-store';
import type {
    ComposeViewState,
    InsertSignatureTask,
    SaveSource,
    SendSource,
} from 'owa-mail-compose-store';
import type {
    CreateAttachmentHandler,
    InsertLinkHandler,
    FileProvidersFilter,
} from 'owa-attachment-filepicker';
import type { AttachmentFileList } from 'owa-attachment-file-types';
import type {
    OnAttachmentCreatedCallback,
    OnAttachmentCanceledOrFailedCallback,
    CreateAttachmentsProperties,
} from 'owa-attachment-well-data';
import type { ClientAttachmentId } from 'owa-client-ids';
import type { Signature } from 'owa-mail-signature';
import type TempEditorRef from 'owa-editor/lib/utils/TempEditorRef';

export const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule<typeof lazyIndex>(
    () => import(/* webpackChunkName: "MailComposeActions"*/ './lazyIndex'),
    { name: 'MailComposeActions' }
);
export const send: (
    viewState: ComposeViewState,
    targetWindow: Window,
    source: SendSource,
    editorRef?: TempEditorRef | undefined
) => Promise<any> = wrapLazy(lazyModule, 'send');
export const save: (
    viewState: ComposeViewState,
    saveSource: SaveSource,
    isAutoSave?: boolean | undefined,
    editorRef?: TempEditorRef | undefined
) => Promise<any> = wrapLazy(lazyModule, 'save');
export const runPostOpenTasks: (
    viewState: ComposeViewState,
    targetWindow?: Window | undefined
) => Promise<any> = wrapLazy(lazyModule, 'runPostOpenTasks');
export const switchBodyType: (viewState: ComposeViewState, targetWindow: Window) => Promise<any> =
    wrapLazy(lazyModule, 'switchBodyType');
export const discard: (
    viewState: ComposeViewState,
    targetWindow?: Window | undefined
) => Promise<any> = wrapLazy(lazyModule, 'discard');
export const upConvert: (
    composeViewState: ComposeViewState,
    targetWindow: Window,
    editorRef?: TempEditorRef | undefined
) => Promise<any> = wrapLazy(lazyModule, 'upConvert');
export const insertSignature: (
    viewState: ComposeViewState,
    param?: string | InsertSignatureTask | Signature | undefined,
    replaceExisting?: boolean | undefined
) => Promise<any> = wrapLazy(lazyModule, 'insertSignature');
export const insertSignatureForAddin: (
    viewState: ComposeViewState,
    signature: string
) => Promise<any> = wrapLazy(lazyModule, 'insertSignatureForAddin');
export const popoutCompose: (
    viewState: ComposeViewState,
    targetWindow?: Window | undefined,
    freezeDryId?: string | undefined
) => Promise<any> = wrapLazy(lazyModule, 'popoutCompose');
export const createAttachments: (
    files: AttachmentFileList,
    composeViewState: ComposeViewState,
    properties: CreateAttachmentsProperties,
    targetWindow: Window,
    onAllAttachmentsProcessed?: ((attachmentId: ClientAttachmentId) => void) | undefined,
    onAttachmentCreatedCallback?: OnAttachmentCreatedCallback | undefined,
    onAttachmentCanceledOrFailedCallback?: OnAttachmentCanceledOrFailedCallback | undefined,
    tryAgainCallback?: (() => void) | undefined
) => Promise<any> = wrapLazy(lazyModule, 'createAttachments');
export const clickAttachButton: (
    isInline: boolean,
    attachmentHandler: CreateAttachmentHandler,
    linkHandler: InsertLinkHandler,
    viewState: ComposeViewState,
    targetWindow: Window,
    fileProvidersFilter?: FileProvidersFilter | undefined
) => Promise<any> = wrapLazy(lazyModule, 'clickAttachButton');
export const setFromAddress: (
    composeViewState: ComposeViewState,
    sendAsAddress: string,
    sendAsName: string,
    targetWindow: Window
) => Promise<any> = wrapLazy(lazyModule, 'setFromAddress');
export const onClickFromMenu: (composeViewState: ComposeViewState) => Promise<any> = wrapLazy(
    lazyModule,
    'onClickFromMenu'
);
export const onExitMailMerge: (
    viewState: ComposeViewState,
    targetWindow: Window,
    isSendDropdown?: boolean | undefined
) => Promise<any> = wrapLazy(lazyModule, 'onExitMailMerge');
export const onSetupMailMerge: (
    viewState: ComposeViewState,
    targetWindow: Window,
    isSendDropdown?: boolean | undefined
) => Promise<any> = wrapLazy(lazyModule, 'onSetupMailMerge');

// import owa-options-message-options to load the message options not included in sessionData

const options: LazyActionOptions = {
    captureBundleTime: true,
};

export const lazyReplyToMessage: LazyAction<typeof lazyIndex.replyToMessage, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.replyToMessage, options);
export const lazyOpenNewComposeFromReferenceItem: LazyAction<
    typeof lazyIndex.openNewComposeFromReferenceItem,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openNewComposeFromReferenceItem, options);
export const lazyForwardMessage: LazyAction<typeof lazyIndex.forwardMessage, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.forwardMessage, options);
export const lazyCloseCompose: LazyAction<typeof lazyIndex.closeCompose, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.closeCompose);
export const lazyDiscardCompose: LazyAction<typeof lazyIndex.discardCompose, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.discardCompose);
export const lazyLoadDraftToCompose: LazyAction<
    typeof lazyIndex.loadDraftToCompose,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.loadDraftToCompose);
export const lazyOpenCompose: LazyAction<typeof lazyIndex.openCompose, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.openCompose);
export const lazyOpenComposeAndChangeFrom: LazyAction<
    typeof lazyIndex.openComposeAndChangeFrom,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.openComposeAndChangeFrom);
export const lazyToggleFrom: LazyAction<typeof lazyIndex.toggleFrom, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.toggleFrom);
export const lazyChangeComposeMailboxInfo: LazyAction<
    typeof lazyIndex.changeComposeMailboxInfo,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.changeComposeMailboxInfo);
export const lazyTrySaveAndCloseCompose: LazyAction<
    typeof lazyIndex.trySaveAndCloseCompose,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.trySaveAndCloseCompose);
export const lazyTrySendMessageFromAddinApi: LazyAction<
    typeof lazyIndex.trySendMessageFromAddinApi,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.trySendMessageFromAddinApi);
export const lazyMoveComposeToTab: LazyAction<
    typeof lazyIndex.moveComposeToTab,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.moveComposeToTab);
export const lazyTryCreateResendDraft: LazyAction<
    typeof lazyIndex.tryCreateResendDraft,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.tryCreateResendDraft);
export const lazyReplyToApprovalMessage: LazyAction<
    typeof lazyIndex.replyToApprovalMessage,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.replyToApprovalMessage);
export const lazyOnEditDraftButtonClicked: LazyAction<
    typeof lazyIndex.onEditDraftButtonClicked,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.onEditDraftButtonClicked);
export const lazyTrySaveMessage: LazyAction<typeof lazyIndex.trySaveMessage, typeof lazyModule> =
    new LazyAction(lazyModule, m => m.trySaveMessage);
export const lazyInsertPlainTextContent: LazyAction<
    typeof lazyIndex.insertPlainTextContent,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.insertPlainTextContent);
export type { ComposeViewStateInitOptions } from './schema/ComposeViewStateInitOptions';
export type { ReplyToMessageInitOptions } from './schema/ReplyToMessageInitOptions';

// Delay loaded utilities
export const lazyGetToCcRecipientsForReply: LazyImport<
    typeof lazyIndex.getToCcRecipientsForReply,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getToCcRecipientsForReply);
export const lazyReplyToMessageAndCreateViewState: LazyImport<
    typeof lazyIndex.replyToMessageAndCreateViewState,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.replyToMessageAndCreateViewState);
export const lazyCreateAttachments: LazyImport<
    typeof lazyIndex.createAttachments,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.createAttachments);
export const lazyExtractComposePopoutData: LazyImport<
    typeof lazyIndex.extractComposePopoutData,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.extractComposePopoutData);

export const lazyPopoutComposeAndWaitForProjection: LazyImport<
    typeof lazyIndex.popoutComposeAndWaitForProjection,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.popoutComposeAndWaitForProjection);

export const lazyTryGetSmimeViewState: LazyImport<
    typeof lazyIndex.tryGetSmimeViewState,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.tryGetSmimeViewState);
export const lazyGetActingMailboxInfoForNewMessage: LazyImport<
    typeof lazyIndex.getActingMailboxInfoForNewMessage,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getActingMailboxInfoForNewMessage);
export const lazyGetRespondSubject: LazyImport<
    typeof lazyIndex.getRespondSubject,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getRespondSubject);
export const lazyGetRespondSubjectPrefix: LazyImport<
    typeof lazyIndex.getRespondSubjectPrefix,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getRespondSubjectPrefix);
export const lazyWaitForServiceId: LazyAction<
    typeof lazyIndex.waitForServiceId,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.waitForServiceId);
export const lazyCreateComposeInitPropsForLoadingDraft: LazyAction<
    typeof lazyIndex.createComposeInitPropsForLoadingDraft,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.createComposeInitPropsForLoadingDraft);
export const lazyAddSendFailNotification: LazyImport<
    typeof lazyIndex.addSendFailNotification,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.addSendFailNotification);
export const lazySetAsyncSendState: LazyAction<
    typeof lazyIndex.setAsyncSendState,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.setAsyncSendState);
export const lazyReuseOrCreateReplyForBizchat: LazyImport<
    typeof lazyIndex.reuseOrCreateReplyForBizchat,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.reuseOrCreateReplyForBizchat);
export const lazySetRecipientsInWell: LazyImport<
    typeof lazyIndex.setRecipientsInWell,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.setRecipientsInWell);
export const lazyGetSimpleRecipientsFromWell: LazyImport<
    typeof lazyIndex.getSimpleRecipientsFromWell,
    typeof lazyModule
> = new LazyImport(lazyModule, m => m.getSimpleRecipientsFromWell);
export const lazyBuildReplyForwardMessageBody: LazyAction<
    typeof lazyIndex.buildReplyForwardMessageBody,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.buildReplyForwardMessageBody);
export const lazyGetAttachmentFilesFromItemAttachments: LazyAction<
    typeof lazyIndex.getAttachmentFilesFromItemAttachments,
    typeof lazyModule
> = new LazyAction(lazyModule, m => m.getAttachmentFilesFromItemAttachments);

export { default as onAddinPictureDeleted } from './actions/onAddinPictureDeleted';
registerLazyOrchestrator(
    onAddinPictureDeleted,
    lazyModule,
    m => m.onAddinPictureDeletedOrchestrator
);
registerLazyOrchestrator(afterCancelDelaySend, lazyModule, m => m.afterCancelDelaySendOrchestrator);
